/* eslint-disable @typescript-eslint/naming-convention */
import { AfterViewInit, Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, first, map, takeUntil, tap } from 'rxjs/operators';

import { addVehicleBindQuery } from '@amfam/policy/add-vehicle/data-access';
import { policiesQuery } from '@amfam/policy/data-access';
import { AnalyticsFacade } from '@amfam/shared/analytics';
import { Agent, agentQuery } from '@amfam/shared/utility/agent';
import { BrandSelectors } from '@amfam/shared/utility/brand';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import { CopyService } from '@amfam/shared/utility/shared-services';
import { DsModalService, LoadingSpinnerService } from '@amfam/ui-kit';

import * as VehicleInfoActions from '../+state/add-vehicle-feature.actions';
import { addVehicleExpectationSettingNotification } from '../+state/add-vehicle-feature.actions';
import { addVehicleFeatureQuery } from '../+state/add-vehicle-feature.selectors';
import { Expectation, ExpectationContent, VinContent } from '../models';

@Component({
  selector: 'ds-vehicle-expectation-setting',
  templateUrl: './vehicle-expectation-setting.component.html',
  styleUrls: ['./vehicle-expectation-setting.component.scss'],
  standalone: false
})
export class VehicleExpectationSettingComponent implements OnInit, OnDestroy, AfterViewInit {
  @Output() exitEvent = new EventEmitter<boolean>();
  @Output() errorEvent = new EventEmitter<unknown>();
  isPartner$ = this.store.select(BrandSelectors.selectIsPartner);
  expectations: Expectation[];
  expectationContent: ExpectationContent;
  vinContent: VinContent;
  findVinFlowModalId = this.modalService.createModalId('findVinFlowModalId');
  agentSelectorModalID = this.modalService.createModalId('agentSelectorModalId');
  public policyAgents$ = this.store.select(
    addVehicleFeatureQuery.selectAdvanceAutoPolicyProducingAgents
  );
  public autoPolicies$ = this.store.select(policiesQuery.selectAutoPolicies);
  titleHolders$: Observable<string>;
  plural = false;

  private stop$: Subject<void> = new Subject<void>();
  private agentsLoaded$ = this.store.select(agentQuery.getAgentsLoaded);

  constructor(
    private copyService: CopyService,
    private store: Store,
    private modalService: DsModalService,
    private loader: LoadingSpinnerService,
    private analyticsFacade: AnalyticsFacade
  ) {}

  ngOnInit(): void {
    this.expectations = this.copyService.getCopy('policy.addVehicleExpectations');
    this.expectationContent = this.copyService.getCopy('policy.addVehicleExpectationsContent');
    this.vinContent = this.copyService.getCopy('policy.findVIN');
    this.handleANSError();
    this.getTitleHolders();
    this.analyticsFacade.trackEvent({
      event: 'add_vehicle_start',
      form_name: 'Add Vehicle Flow',
      product_line: 'auto'
    });
  }

  ngAfterViewInit() {
    this.store.dispatch(
      VehicleInfoActions.addVehicleSendAnalytics({
        payload: { pageAnalyticName: 'PreStartChecklist' }
      })
    );
  }

  getTitleHolders() {
    this.titleHolders$ = this.store.select(policiesQuery.selectRolesforAdvanceAutoPolicy).pipe(
      filter(policyRoles => !!policyRoles && policyRoles.length > 0),
      first(),
      map(policyRoles => policyRoles[0].roles),
      map(roles => {
        const pni = roles.find(role => role.type.toLowerCase() === 'primarynamedinsured');
        const sni = roles.find(role => role.type.toLowerCase() === 'secondarynamedinsured');
        this.plural = !!sni;
        return `${pni.firstName} ${pni.lastName}  ${
          sni ? ' or ' + sni.firstName + ' ' + sni.lastName : ''
        } ${sni ? 'are ' : 'is '}`;
      })
    );
  }

  goBack() {
    this.store.dispatch(fromRouterActions.Back());
  }

  sendToMyAgent() {
    this.analyticsFacade.trackEvent({
      event: 'agent_referral',
      form_name: 'Replace Vehicle'
    });
    combineLatest([this.policyAgents$, this.agentsLoaded$])
      .pipe(
        filter(([_, agentsLoaded]) => !!agentsLoaded),
        first(),
        tap(([agents, _]) => {
          const hasAgencyEmail = agents.some(
            agent => agent.agencyEmails && agent.agencyEmails.length > 0
          );
          if (agents && agents.length > 1 && !hasAgencyEmail) {
            this.modalService.open(this.agentSelectorModalID);
          } else {
            this.notify(agents[0]);
          }
        })
      )
      .subscribe();
  }

  agentConfirmationFromModal(agent: Agent) {
    if (agent) {
      this.modalService.close(this.agentSelectorModalID);
      this.notify(agent);
    }
  }

  notify(agent: Agent) {
    this.loader.start({ blockActions: true });
    this.autoPolicies$
      .pipe(
        filter(policies => !!policies && policies.length > 0),
        first(),
        tap(policies => {
          const policyNumbers = policies
            .filter(policy => policy.assignedProducer.producerIdNum === agent.id)
            .map(p => p.policyNumber);

          this.store.dispatch(
            addVehicleExpectationSettingNotification({
              payload: {
                agent,
                policyNumber: policyNumbers.length > 0 ? policyNumbers.join(',') : policyNumbers[0]
              }
            })
          );
        })
      )
      .subscribe();
  }

  cancel() {
    this.exitEvent.emit(true);
  }

  findVIN(e) {
    e.preventDefault();

    this.store.dispatch(
      VehicleInfoActions.addVehicleSendAnalytics({
        payload: { buttonAnalyticName: 'PreStartChecklist:FindVIN' }
      })
    );

    this.openModal().then(() => {
      setTimeout(() => {
        this.store.dispatch(
          VehicleInfoActions.addVehicleSendAnalytics({
            payload: { pageAnalyticName: 'FindVIN' }
          })
        );
      });
    });
  }

  continue() {
    this.store.dispatch(
      fromRouterActions.Go({
        path: ['/policies/add-vehicle/year-vin']
      })
    );
  }

  ngOnDestroy(): void {
    this.loader.stop();
    this.stop$.next();
    this.stop$.complete();
  }

  private openModal = async () => {
    this.modalService.open(this.findVinFlowModalId);
  };

  private handleANSError() {
    this.store
      .select(addVehicleBindQuery.selectVehicleANSNotificationError)
      .pipe(takeUntil(this.stop$))
      .subscribe(agentNotificationError => {
        if (agentNotificationError) {
          this.loader.stop();
          this.errorEvent.emit();
        }
      });
  }
}
